import { type CpuStats, type GameId, LEVELS } from '@xox/shared'
import { type ReactNode, useEffect, useState } from 'react'
import { OnlineList } from '../components/arena/OnlineList'
import { C4, XOX } from '../components/duel/defs'
import { GamePoster } from '../components/GamePoster'
import { PlayingAs } from '../components/PlayingAs'
import { ResumeCard } from '../components/ResumeCard'
import { QuestList } from '../components/today/QuestList'
import { TodayCard } from '../components/today/TodayCard'
import { Card } from '../components/ui'
import { useFetch } from '../hooks/useFetch'
import { loadProgress, type Progress, summarizeProgress } from '../ice/progress'
import { plural } from '../lib/format'
import { GAMES, type GameInfo } from '../lib/games'
import { useSession } from '../store/session'
import { useLoadToday, useToday } from '../store/today'

/** "N challengers vs BOT · nobody has won" (or the real win/draw counts). */
function bossLine(stats: CpuStats | null, bot: string, teaser: string): ReactNode {
  if (!stats) return <p>{teaser}</p>
  const { games, wins, draws } = stats.totals
  return (
    <p>
      <strong>{plural(games, 'challenger')}</strong> vs {bot} ·{' '}
      {wins > 0
        ? `${plural(wins, 'win')} · ${plural(draws, 'draw')}`
        : draws > 0
          ? `0 wins · ${plural(draws, 'draw')}`
          : 'nobody has won'}
    </p>
  )
}

/**
 * The arena home, "Today": who you are, today's dungeon and quests, who is around, and one
 * poster per game.
 */
export function GamePicker() {
  const me = useSession((s) => s.me)
  const load = useSession((s) => s.load)
  useEffect(() => {
    void load()
  }, [load])
  useLoadToday()
  const today = useToday((s) => s.data)
  const todayError = useToday((s) => s.error)
  const { data: xoxStats } = useFetch(XOX.api.cpuStats)
  const { data: c4Stats } = useFetch(C4.api.cpuStats)
  const [progress, setProgress] = useState<Progress>({})
  useEffect(() => {
    setProgress(loadProgress())
  }, [])
  const ice = summarizeProgress(progress, LEVELS)

  const run = today?.run ?? null
  const hooks: Record<GameInfo['to'], ReactNode> = {
    '/xox': bossLine(xoxStats, XOX.bot.nickname, 'Think you can beat a perfect player?'),
    '/c4': bossLine(c4Stats, C4.bot.nickname, 'It sees four moves ahead. Do you?'),
    '/cycles': <p>Up to 8 riders, one arena, walls everywhere. Last cycle standing wins.</p>,
    '/dungeon': (
      <p>
        {run
          ? run.status === 'active'
            ? `Today: on floor ${run.floor} · resume`
            : `Today: ${run.score} pts`
          : today
            ? 'Not played today'
            : 'A new dungeon every day. One run. Everyone.'}
      </p>
    ),
    '/ice': (
      <div className="flex flex-col gap-1.5">
        <div className="h-1.5 overflow-hidden rounded-full bg-white/25">
          <div
            className="h-full rounded-full bg-white"
            style={{ width: `${(ice.cleared / LEVELS.length) * 100}%` }}
          />
        </div>
        <p>
          <strong>
            {ice.cleared}/{LEVELS.length}
          </strong>{' '}
          cleared ·{' '}
          {ice.next ? `Next: #${ice.nextNumber} ${ice.next.name}` : 'every level cleared!'}
        </p>
      </div>
    ),
  }
  const activeRooms = me
    ? (Object.entries(me.activeRooms) as [GameId, string | null][]).filter(
        (entry): entry is [GameId, string] => entry[1] !== null,
      )
    : []

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 py-4">
      <PlayingAs eyebrow="Welcome back" />
      {activeRooms.map(([game, code]) => (
        <ResumeCard key={game} game={game} code={code} />
      ))}
      {todayError && <p className="text-sm text-rose-600">{todayError}</p>}

      <section aria-label="Today" className="grid gap-4 sm:grid-cols-2">
        <TodayCard today={today} progress={me?.progress} />
        <QuestList quests={today?.quests ?? null} />
      </section>

      <Card className="flex flex-col gap-2">
        <h2 className="font-semibold">Here now</h2>
        <OnlineList />
      </Card>

      <ul className="grid gap-4 sm:grid-cols-2">
        {GAMES.map((g) => (
          <li key={g.to}>
            <GamePoster game={g} hook={hooks[g.to]} color={me?.color} />
          </li>
        ))}
      </ul>
      <p className="text-center text-xs text-slate-500">
        No sign-up. Share a link, play in seconds.
      </p>
    </div>
  )
}
